import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageLoader } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { TaskFormDialog } from "@/components/tasks/TaskFormDialog";
import { TaskRow } from "@/components/tasks/TaskRow";
import { PROJECT_STATUS, fmtDate } from "@/lib/taskUi";
import { ArrowLeft, Plus, Users, Trash2, Clock, Pencil, Search, Crown, UserMinus } from "lucide-react";
import { toast } from "sonner";
import { PinToReport } from "@/components/common/PinToReport";
import { MilestonesPanel } from "@/components/projects/MilestonesPanel";
import { ProjectCategorySelect } from "@/components/projects/ProjectCategorySelect";
import { TemplateSyncButton } from "@/components/projects/TemplateSyncButton";
import { CopyStructureButton } from "@/components/projects/CopyStructureButton";
import { ProjectTaskGroups } from "@/components/projects/ProjectTaskGroups";
import { DefaultAssignmentsDialog } from "@/components/projects/DefaultAssignmentsDialog";

const TABS = ["Overview", "Tasks", "Milestones", "Team", "Activity"];
const PRIORITY = { low: "Low", normal: "Normal", high: "High", critical: "Critical" };

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [isTemplate, setIsTemplate] = useState(false);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "Overview");
  const [addTask, setAddTask] = useState(false);
  const [editing, setEditing] = useState(false);
  const { user, isAdmin, isSuper, isGroupAdmin, currentAccount } = useAuth();

  const load = useCallback(async () => {
    try {
      const [p, t] = await Promise.all([api.get(`/projects/${id}`), api.get(`/projects/${id}/tasks`)]);
      setData(p.data); setTasks(t.data.items || []); setIsTemplate(!!t.data.is_template);
    } catch (e) { toast.error(apiError(e)); navigate("/projects"); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <PageLoader />;
  const p = data.project, st = p.stats;
  const canManage = p.can_edit ?? (isAdmin || isSuper || user?.id === p.created_by || user?.id === p.owner_id || isGroupAdmin);
  const daysRemaining = p.target_date ? Math.ceil((new Date(p.target_date) - new Date()) / 86400000) : null;
  const scopeLabel = p.is_personal ? "Personal Project · private to the owner" : p.organization_id ? (p.organization_name || "District") : `${currentAccount?.name || "Client Account"} · Account-wide`;

  const setStatus = async (v) => { try { await api.patch(`/projects/${id}`, { status: v }); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async () => {
    if (!window.confirm(`Delete project "${p.name}"? This cannot be undone. Its tasks remain but will no longer be grouped under this project.`)) return;
    try { await api.delete(`/projects/${id}`); toast.success("Project deleted"); navigate("/projects"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-5xl">
      <button onClick={() => navigate("/projects")} className="flex items-center gap-1 text-sm text-slate-400 hover:text-brand mb-3" data-testid="back-to-projects"><ArrowLeft className="w-4 h-4" /> Projects</button>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-bold text-slate-900 truncate" data-testid="project-title">{p.name}</h1>
          <p className="text-sm text-slate-400"><span className={p.person_name ? "font-medium text-slate-600" : ""} data-testid="project-person-name">{p.person_name || p.human_id}</span> · Owner {p.owner_name || "—"} · {scopeLabel}</p>
        </div>
        <div className="flex items-center gap-2">
          <PinToReport type="project" id={id} />
          {p.can_edit ? (
            <Select value={p.status} onValueChange={setStatus}>
              <SelectTrigger className="w-40 h-9" data-testid="project-status-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{Object.entries(PROJECT_STATUS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          ) : <span className="text-xs font-semibold rounded-full px-3 py-1.5 bg-slate-100 text-slate-600" data-testid="project-status-badge">{PROJECT_STATUS[p.status] || p.status}</span>}
          {canManage && <Button size="sm" className="h-9 bg-brand hover:bg-brand-strong" onClick={() => setEditing(true)} data-testid="edit-project-btn"><Pencil className="w-4 h-4 mr-1.5" />Edit Project</Button>}
          {canManage && <Button variant="outline" size="sm" onClick={del} data-testid="project-delete-btn" className="h-9 text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"><Trash2 className="w-4 h-4 mr-1" />Delete</Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-5">
        <Metric label="Progress" value={`${st.progress}%`} />
        <Metric label="Open Tasks" value={st.total_tasks - st.completed_tasks} />
        <Metric label="Completed" value={st.completed_tasks} />
        <Metric label="Overdue" value={st.overdue_tasks} tone={st.overdue_tasks ? "rose" : ""} />
        <Metric label="Milestones" value={data.milestones.length} />
        <Metric label="Days Left" value={daysRemaining == null ? "—" : daysRemaining} tone={daysRemaining != null && daysRemaining < 0 ? "rose" : ""} />
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-5"><div className="h-full bg-brand rounded-full" style={{ width: `${st.progress}%` }} data-testid="project-progress-bar" /></div>

      <div className="flex items-center gap-1 border-b border-slate-200 mb-5 overflow-x-auto" data-testid="project-tabs">
        {TABS.map((tt) => (
          <button key={tt} onClick={() => setTab(tt)} data-testid={`ptab-${tt.toLowerCase()}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === tt ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{tt}</button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="space-y-3" data-testid="project-overview">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            {p.description ? <p className="text-sm text-slate-600 whitespace-pre-wrap">{p.description}</p>
              : <div className="text-sm text-slate-400 flex items-center gap-2">No description yet.{canManage && <button className="text-brand font-medium hover:underline" onClick={() => setEditing(true)} data-testid="add-description-btn">Add Description</button>}</div>}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4 text-sm">
              <Field label="Start" value={fmtDate(p.start_date) || "—"} />
              <Field label="Target" value={fmtDate(p.target_date) || "—"} />
              <Field label="Priority" value={PRIORITY[p.priority] || p.priority || "—"} />
              <Field label="Category" testId="overview-category" value={p.category_name || "Uncategorized"} />
              <Field label="Team Size" testId="overview-team-size" value={`${(p.team || []).length} ${((p.team || []).length === 1) ? "member" : "members"}`} />
              <Field label="Scope" testId="overview-scope" value={p.is_personal ? "Personal" : p.organization_id ? (p.organization_name || "District") : "Account-wide"} />
              <Field label="Organization" value={p.organization_name || (currentAccount?.name || "—")} />
            </div>
          </div>
        </div>
      )}

      {tab === "Tasks" && <ProjectTasks tasks={tasks} milestones={data.milestones} userId={user?.id} onChanged={load} onAdd={() => setAddTask(true)} backTo={{ from: `/projects/${id}?tab=Tasks`, label: p.person_name ? `${p.name} — ${p.person_name}` : p.name }} projectId={id} canManage={!!p.can_edit} isTemplate={isTemplate} />}

      {tab === "Milestones" && <MilestonesPanel projectId={id} milestones={data.milestones} canManage={!!p.can_manage_milestones} onChanged={load} />}

      {tab === "Team" && <TeamTab project={p} canManage={canManage} onChanged={load} />}

      {tab === "Activity" && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="project-activity">
          {(p.history || []).slice().reverse().map((h, i) => (
            <p key={i} className="text-xs text-slate-500 py-1"><span className="font-medium text-slate-700">{h.message || h.action.replace(/_/g, " ")}</span> · {h.by_name} · {new Date(h.at).toLocaleString()}</p>
          ))}
          {!(p.history || []).length && <p className="text-sm text-slate-400">No activity yet.</p>}
        </div>
      )}

      {editing && <EditProject project={p} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
      <TaskFormDialog open={addTask} onClose={() => setAddTask(false)} onCreated={load} defaults={{ project_id: id }} title="Add Task to Project" milestones={data.milestones} templateMode={isTemplate} />
    </div>
  );
}

function Metric({ label, value, tone }) {
  return <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm text-center">
    <p className={`text-xl font-display font-bold ${tone === "rose" ? "text-rose-600" : "text-slate-900"}`}>{value}</p>
    <p className="text-[11px] text-slate-500">{label}</p></div>;
}
function Field({ label, value, testId }) { return <div data-testid={testId}><p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p><p className="text-slate-700">{value}</p></div>; }

const DONE = new Set(["completed", "cancelled"]);
const PRIO_ORDER = { critical: 0, high: 1, normal: 2, low: 3 };

function ProjectTasks({ tasks, milestones, userId, onChanged, onAdd, backTo, projectId, canManage, isTemplate }) {
  const [daOpen, setDaOpen] = useState(false);
  const [quick, setQuick] = useState("all");
  const [assignee, setAssignee] = useState("all");
  const [milestone, setMilestone] = useState("all");
  const [sort, setSort] = useState("manual");
  const [grouped, setGrouped] = useState(true);

  const counts = {
    all: tasks.length,
    mine: tasks.filter((t) => t.assignee_id === userId).length,
    unassigned: tasks.filter((t) => !t.assignee_id).length,
    open: tasks.filter((t) => !DONE.has(t.status)).length,
    completed: tasks.filter((t) => t.status === "completed").length,
  };
  const assignees = Array.from(new Map(tasks.filter((t) => t.assignee_id).map((t) => [t.assignee_id, t.assignee_name || "Unknown"])).entries());

  let list = tasks.filter((t) => {
    if (quick === "mine" && t.assignee_id !== userId) return false;
    if (quick === "unassigned" && t.assignee_id) return false;
    if (quick === "open" && DONE.has(t.status)) return false;
    if (quick === "completed" && t.status !== "completed") return false;
    if (assignee !== "all" && (assignee === "none" ? t.assignee_id : t.assignee_id !== assignee)) return false;
    if (milestone !== "all" && (milestone === "none" ? t.milestone_id : t.milestone_id !== milestone)) return false;
    return true;
  });
  list = [...list].sort((a, b) => {
    if (sort === "manual") {
      const ao = a.project_order ?? Infinity, bo = b.project_order ?? Infinity;
      return ao === bo ? (a.created_at || "").localeCompare(b.created_at || "") : ao - bo;
    }
    if (sort === "due") return (a.due_date || "9999").localeCompare(b.due_date || "9999");
    if (sort === "priority") return (PRIO_ORDER[a.importance] ?? 2) - (PRIO_ORDER[b.importance] ?? 2);
    if (sort === "updated") return (b.updated_at || "").localeCompare(a.updated_at || "");
    if (sort === "assignee") return (a.assignee_name || "").localeCompare(b.assignee_name || "");
    // default: open first, then due asc, then priority
    const ao = DONE.has(a.status) ? 1 : 0, bo = DONE.has(b.status) ? 1 : 0;
    if (ao !== bo) return ao - bo;
    const dd = (a.due_date || "9999").localeCompare(b.due_date || "9999");
    if (dd) return dd;
    return (PRIO_ORDER[a.importance] ?? 2) - (PRIO_ORDER[b.importance] ?? 2);
  });

  const CHIPS = [["all", "All Tasks"], ["mine", "Assigned to Me"], ["unassigned", "Unassigned"], ["open", "Open"], ["completed", "Completed"]];
  const renderList = (items) => items.length
    ? items.map((t) => <TaskRow key={t.id} task={t} onChanged={onChanged} showAssignee backTo={backTo} showVisibility templateMode={isTemplate} />)
    : <div className="px-4 py-6 text-center text-sm text-slate-400">No tasks match this view.</div>;

  return (
    <div data-testid="project-tasks">
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        {CHIPS.map(([k, l]) => (
          <button key={k} onClick={() => setQuick(k)} data-testid={`task-filter-${k}`}
            className={`text-xs rounded-full px-3 py-1.5 border ${quick === k ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {l} <span className={quick === k ? "text-white/80" : "text-slate-400"}>{counts[k]}</span>
          </button>
        ))}
        <div className="ml-auto flex gap-2">{isTemplate && canManage && <Button size="sm" variant="outline" onClick={() => setDaOpen(true)} data-testid="default-assign-btn">Default Assignments</Button>}
          <Button size="sm" className="bg-brand hover:bg-brand-strong" onClick={onAdd} data-testid="project-add-task"><Plus className="w-4 h-4 mr-1" />Add Task</Button></div>
        {daOpen && <DefaultAssignmentsDialog projectId={projectId} onClose={() => setDaOpen(false)} onDone={() => { setDaOpen(false); onChanged(); }} />}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Select value={assignee} onValueChange={setAssignee}><SelectTrigger className="h-8 w-40 text-xs" data-testid="task-assignee-filter"><SelectValue placeholder="Assignee" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">All assignees</SelectItem><SelectItem value="none">Unassigned</SelectItem>{assignees.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}</SelectContent></Select>
        <Select value={milestone} onValueChange={setMilestone}><SelectTrigger className="h-8 w-44 text-xs" data-testid="task-milestone-filter"><SelectValue placeholder="Milestone" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">All milestones</SelectItem><SelectItem value="none">No milestone</SelectItem>{milestones.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}</SelectContent></Select>
        <Select value={sort} onValueChange={setSort}><SelectTrigger className="h-8 w-40 text-xs" data-testid="task-sort"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="manual">Sort: Manual order</SelectItem><SelectItem value="default">Sort: Smart</SelectItem><SelectItem value="due">Due date</SelectItem><SelectItem value="priority">Priority</SelectItem><SelectItem value="assignee">Assignee</SelectItem><SelectItem value="updated">Recently updated</SelectItem></SelectContent></Select>
        <label className="flex items-center gap-1.5 text-xs text-slate-500 ml-1"><input type="checkbox" checked={grouped} onChange={(e) => setGrouped(e.target.checked)} data-testid="task-group-milestone" />Group by milestone</label>
        <span className="text-xs text-slate-400 ml-auto" data-testid="task-visible-count">{list.length} shown</span>
      </div>

      {grouped && canManage && !(sort === "manual" && quick === "all" && assignee === "all" && milestone === "all") && <p className="text-xs text-slate-400 mb-2" data-testid="task-drag-hint">Clear filters and use Manual order to drag tasks.</p>}
      {grouped ? (
        <ProjectTaskGroups projectId={projectId} list={list} milestones={milestone === "all" ? milestones : milestones.filter((m) => m.id === milestone)}
          canDrag={canManage && sort === "manual" && quick === "all" && assignee === "all" && milestone === "all"} onChanged={onChanged} backTo={backTo} templateMode={isTemplate} />
      ) : (
        <div className="rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {tasks.length ? renderList(list) : <EmptyState icon={Clock} title="No tasks" description="Add tasks to this project." />}
        </div>
      )}
    </div>
  );
}

function EditProject({ project, onClose, onSaved }) {
  const [users, setUsers] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: project.name || "", description: project.description || "",
    start_date: (project.start_date || "").slice(0, 10), target_date: (project.target_date || "").slice(0, 10),
    priority: project.priority || "normal", status: project.status || "active",
    scope: project.organization_id ? "district" : "account", organization_id: project.organization_id || "",
    owner_id: project.owner_id || "", category_id: project.category_id || null,
  });
  const [cats, setCats] = useState([]);
  const [canCreateCat, setCanCreateCat] = useState(false);
  useEffect(() => {
    api.get("/tasks/assignable-users").then((r) => setUsers(r.data.items || [])).catch(() => {});
    api.get("/agency-districts").then((r) => setDistricts(r.data.items || [])).catch(() => {});
    api.get("/project-categories").then((r) => { setCats((r.data.items || []).filter((c) => !!c.is_personal === !!project.is_personal)); setCanCreateCat(project.is_personal || !!r.data.can_create_shared); }).catch(() => {});
  }, []);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) return toast.error("Project name is required");
    if (f.start_date && f.target_date && f.target_date < f.start_date) return toast.error("Target date cannot precede start date");
    if (f.scope === "district" && !f.organization_id) return toast.error("Please select a District for a district-scoped project");
    const payload = {
      name: f.name.trim(), description: f.description, priority: f.priority, status: f.status,
      start_date: f.start_date || null, target_date: f.target_date || null,
      organization_id: f.scope === "account" ? null : f.organization_id,
    };
    if (project.is_personal) delete payload.organization_id;
    if ((f.category_id || null) !== (project.category_id || null)) payload.category_id = f.category_id || "";
    setSaving(true);
    try {
      await api.patch(`/projects/${project.id}`, payload);
      if (!project.is_personal && f.owner_id && f.owner_id !== project.owner_id) await api.post(`/projects/${project.id}/owner`, { user_id: f.owner_id });
      toast.success("Project updated"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-project-dialog">
        <DialogHeader><DialogTitle>Edit Project</DialogTitle><DialogDescription>Update the project overview, scope and owner.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold">Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-name" /></div>
          <div><Label className="text-xs font-semibold">Description</Label><Textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="edit-description" /></div>
          <div><Label className="text-xs font-semibold">Category</Label>
            <ProjectCategorySelect value={f.category_id} onChange={(v) => set("category_id", v)} cats={cats} setCats={setCats} canCreate={canCreateCat} personal={!!project.is_personal} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Start date</Label><Input type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="edit-start" /></div>
            <div><Label className="text-xs font-semibold">Target date</Label><Input type="date" value={f.target_date} onChange={(e) => set("target_date", e.target.value)} data-testid="edit-target" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Priority</Label>
              <Select value={f.priority} onValueChange={(v) => set("priority", v)}><SelectTrigger data-testid="edit-priority"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{Object.entries(PRIORITY).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs font-semibold">Status</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}><SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{Object.entries(PROJECT_STATUS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></div>
          </div>
          {!project.is_personal && <><div>
            <Label className="text-xs font-semibold">Project Scope</Label>
            <div className="flex flex-col gap-1.5 mt-1">
              <label className="flex items-center gap-2 text-sm"><input type="radio" checked={f.scope === "account"} onChange={() => set("scope", "account")} data-testid="edit-scope-account" />Client Account — account-wide (visible under all your districts)</label>
              <label className="flex items-center gap-2 text-sm"><input type="radio" checked={f.scope === "district"} onChange={() => set("scope", "district")} data-testid="edit-scope-district" />Specific District</label>
              {f.scope === "district" && (
                <Select value={f.organization_id} onValueChange={(v) => set("organization_id", v)}>
                  <SelectTrigger data-testid="edit-district" className="mt-1"><SelectValue placeholder="Select District…" /></SelectTrigger>
                  <SelectContent className="bg-white">{districts.map((d) => <SelectItem key={d.org_id} value={d.org_id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
          </div>
          <div><Label className="text-xs font-semibold">Owner</Label>
            <Select value={f.owner_id} onValueChange={(v) => set("owner_id", v)}><SelectTrigger data-testid="edit-owner"><SelectValue placeholder="Select owner…" /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">{users.filter((u) => u.role !== "candidate").map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} · {(u.role || "").replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></div></>}
          {!project.is_personal && <TemplateSyncButton projectId={project.id} onDone={onSaved} />}
          {project.can_edit && <CopyStructureButton projectId={project.id} onDone={onSaved} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-brand hover:bg-brand-strong" onClick={save} disabled={saving} data-testid="edit-project-save">{saving ? "Saving…" : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TeamTab({ project, canManage, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const members = project.team_members || [];

  useEffect(() => { if (adding) api.get("/tasks/assignable-users").then((r) => setUsers(r.data.items || [])).catch(() => {}); }, [adding]);

  const add = async (uid) => {
    try { await api.post(`/projects/${project.id}/team`, { user_id: uid }); toast.success("Team member added"); setAdding(false); setQ(""); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (uid, name) => {
    try { await api.delete(`/projects/${project.id}/team/${uid}`); toast.success("Removed from team"); onChanged(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.assigned_tasks) {
        if (window.confirm(`${d.message}`)) {
          try { await api.delete(`/projects/${project.id}/team/${uid}`, { params: { force: true } }); toast.success("Removed from team (tasks left assigned)"); onChanged(); }
          catch (e2) { toast.error(apiError(e2)); }
        }
      } else { toast.error(apiError(e)); }
    }
  };
  const makeOwner = async (uid) => {
    if (!window.confirm("Transfer project ownership to this member?")) return;
    try { await api.post(`/projects/${project.id}/owner`, { user_id: uid }); toast.success("Ownership transferred"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const memberIds = new Set(members.map((m) => m.user_id));
  const pick = users.filter((u) => !memberIds.has(u.user_id) && u.role !== "candidate" && ((u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.email || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-3" data-testid="project-team">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{members.length} {members.length === 1 ? "team member" : "team members"}</p>
        {canManage && !project.is_personal && <Button size="sm" className="bg-brand hover:bg-brand-strong" onClick={() => setAdding(true)} data-testid="add-team-member-btn"><Plus className="w-4 h-4 mr-1" />Add Team Member</Button>}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm divide-y divide-slate-100">
        {members.length ? members.map((m) => (
          <div key={m.user_id} className="flex items-center gap-3 px-4 py-3" data-testid={`team-member-${m.user_id}`}>
            <div className="w-8 h-8 rounded-full bg-brand/10 text-brand text-xs font-bold flex items-center justify-center">{(m.name || "?").slice(0, 1)}</div>
            <span className="text-sm font-medium text-slate-700">{m.name}</span>
            {m.user_id === project.owner_id
              ? <span className="text-[10px] rounded-full bg-brand text-white px-2 py-0.5 ml-auto flex items-center gap-1"><Crown className="w-3 h-3" />Owner</span>
              : <span className="text-[10px] rounded-full bg-slate-100 text-slate-500 px-2 py-0.5 ml-auto">Member</span>}
            {canManage && m.user_id !== project.owner_id && (
              <div className="flex items-center gap-1">
                <button onClick={() => makeOwner(m.user_id)} title="Make owner" data-testid={`make-owner-${m.user_id}`} className="text-slate-300 hover:text-brand p-1"><Crown className="w-4 h-4" /></button>
                <button onClick={() => remove(m.user_id, m.name)} title="Remove" data-testid={`remove-member-${m.user_id}`} className="text-slate-300 hover:text-rose-500 p-1"><UserMinus className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        )) : <div className="p-6"><EmptyState icon={Users} title="No team members" description="Add members to collaborate on this project." /></div>}
      </div>

      {adding && (
        <Dialog open onOpenChange={(o) => !o && setAdding(false)}>
          <DialogContent className="bg-white max-w-md" data-testid="add-team-dialog">
            <DialogHeader><DialogTitle>Add Team Member</DialogTitle><DialogDescription>Only internal users in this Client Account within your authorized scope are shown.</DialogDescription></DialogHeader>
            <div className="relative"><Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="pl-8" data-testid="team-search" /></div>
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-50 mt-2">
              {pick.map((u) => (
                <button key={u.user_id} onClick={() => add(u.user_id)} data-testid={`team-pick-${u.user_id}`} className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-brand/10 text-brand text-xs font-bold flex items-center justify-center">{(u.name || "?").slice(0, 1)}</div>
                  <div className="min-w-0"><p className="text-sm text-slate-700 truncate">{u.name} <span className="text-xs text-slate-400">· {(u.role || "").replace(/_/g, " ")}</span></p><p className="text-[11px] text-slate-400 truncate">{u.email}</p></div>
                </button>
              ))}
              {pick.length === 0 && <p className="text-center text-sm text-slate-400 py-6">No eligible users found.</p>}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

